<template>
  <div
    v-if="themes?.length"
    class="section-y"
    data-test-id="giftcard-themes-showcase"
  >
    <Heading
      h-class="section-title"
      :level="2"
      :title="title || t('personalization.theme.showcase.title')"
      use-h-tag
      class="mb-3 md:mb-4 kft-heading--left"
    />

    <!-- same rail as ProductShowcase's isSlider mode (global classes from
         ProductShowcase.vue): scrolls on mobile, four per row with arrows on
         desktop. Wrapped like the V2 event cards so they share the sizing. -->
    <ProductsShowcaseSlider
      with-arrows
      class="product-showcase-cards-container product-showcase-cards-container--slider product-showcase-cards-container--slider-desktop"
    >
      <div
        v-for="theme in themes"
        :key="theme.id"
        class="slider-item slider-item--v2"
      >
        <NuxtLink
          :to="localePath(`/giftcard/theme/${theme.slug}/`)"
          class="group flex w-full flex-col gap-2"
          :data-test-id="`giftcard-theme-card-${theme.slug}`"
        >
          <div class="relative">
            <NuxtImg
              v-if="theme.thumbnailImage"
              :src="theme.thumbnailImage"
              :alt="theme.name"
              loading="lazy"
              class="aspect-4/3 w-full rounded-2xl object-cover transition-opacity group-hover:opacity-90"
            />
            <span
              class="absolute top-2 left-2 flex items-center gap-1 rounded-sm bg-orange-50 px-1.5 py-1 text-overline font-semibold uppercase tracking-wider text-orange-800"
            >
              <Gift class="h-3 w-3" aria-hidden="true" />
              {{ t('personalization.theme.showcase.badge') }}
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-base font-semibold text-stone-800">
              {{ theme.name }}
            </span>
            <span class="text-sm text-stone-600">
              {{ fromPrice }}
            </span>
          </div>
        </NuxtLink>
      </div>
    </ProductsShowcaseSlider>
  </div>
</template>

<script setup lang="ts">
import ProductsShowcaseSlider from '~/components/product/ProductsShowcaseSlider.vue'
import { Gift } from 'lucide-vue-next'
import { GIFTCARD_MIN_AMOUNT } from '~/utils/giftcardAmount'
import { formatMoney } from '~/utils/currencyHelpers'

const { title = '' } = defineProps<{
  /** Defaults to the generic "for every occasion" title. */
  title?: string
}>()

const { t, locale } = useI18n()
const localePath = useLocalePath()

const fromPrice = computed(() =>
  t('personalization.theme.showcase.fromPrice', {
    min: formatMoney(GIFTCARD_MIN_AMOUNT, locale.value, 'EUR', 0),
  })
)

// hidden while the API has no (active) themes - e.g. the endpoint isn't deployed
const { data: themes } = await useGiftcardThemes()
</script>
